import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Plus } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  FilterCondition,
  FilterCriteria,
  MarketingSegment,
  MarketingSegmentDraft,
  useCreateMarketingSegment,
  useUpdateMarketingSegment,
} from '../../queries/marketingSegments';
import { SubscriptionPackage } from '../../queries/subscriptionPackages';
import { ManualUserPicker } from './ManualUserPicker';
import { SegmentConditionEditor } from './SegmentConditionEditor';

interface SegmentFormDialogProps {
  segment: MarketingSegment | null; // null = create
  packages: SubscriptionPackage[];
  onClose: () => void;
}

const EMPTY_CRITERIA: FilterCriteria = { operator: 'AND', conditions: [] };

// A condition added but never filled in used to save happily and then match
// nobody (and, before the builder guarded it, error the preview outright).
// Booleans and numbers are legitimate values - false and 0 included - so only
// an empty list or an empty/absent value counts as unfinished.
function isIncomplete({ value }: FilterCondition): boolean {
  if (Array.isArray(value)) return value.length === 0;
  return value === '' || value === null || value === undefined;
}

export function SegmentFormDialog({ segment, packages, onClose }: SegmentFormDialogProps) {
  const [name, setName] = useState(segment?.name ?? '');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [criteria, setCriteria] = useState<FilterCriteria>(segment?.filter_criteria ?? EMPTY_CRITERIA);
  const [manualUserIds, setManualUserIds] = useState<string[]>(segment?.manual_user_ids ?? []);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateMarketingSegment();
  const update = useUpdateMarketingSegment();
  const isPending = create.isPending || update.isPending;

  // An unfinished condition carries no meaning, so it is dropped on save
  // rather than blocking it - otherwise a segment that already holds one
  // (they used to save happily) can never be edited into a working state,
  // including by switching it to hand-picked users.
  const usableConditions = criteria.conditions.filter((c) => !isIncomplete(c));
  const droppedConditions = criteria.conditions.length - usableConditions.length;
  const canSave = Boolean(name.trim()) && (usableConditions.length > 0 || manualUserIds.length > 0);

  const addCondition = () => {
    setCriteria({ ...criteria, conditions: [...criteria.conditions, { field: 'subscription_status', operator: 'in', value: [] }] });
  };
  const updateCondition = (index: number, condition: FilterCondition) => {
    setCriteria({ ...criteria, conditions: criteria.conditions.map((c, i) => (i === index ? condition : c)) });
  };
  const removeCondition = (index: number) => {
    setCriteria({ ...criteria, conditions: criteria.conditions.filter((_, i) => i !== index) });
  };

  const handleSave = () => {
    setError(null);
    const draft: MarketingSegmentDraft = {
      name,
      description: description || null,
      filter_criteria: { ...criteria, conditions: usableConditions },
      manual_user_ids: manualUserIds,
    };
    const onSettled = { onSuccess: onClose, onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')) };
    if (segment) {
      update.mutate({ ...draft, id: segment.id }, onSettled);
    } else {
      create.mutate(draft, onSettled);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{segment ? 'Edit segment' : 'Create segment'}</Dialog.Title>

          <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto">
            <div>
              <label htmlFor="segment-name" className="mb-1 block text-sm font-medium">Name</label>
              <input
                id="segment-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="segment-description" className="mb-1 block text-sm font-medium">Description</label>
              <textarea
                id="segment-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium">Filter conditions</span>
                <select
                  value={criteria.operator}
                  onChange={(e) => setCriteria({ ...criteria, operator: e.target.value as 'AND' | 'OR' })}
                  className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                >
                  <option value="AND">Match ALL conditions</option>
                  <option value="OR">Match ANY condition</option>
                </select>
              </div>
              <div className="space-y-2">
                {criteria.conditions.map((condition, i) => (
                  <SegmentConditionEditor
                    key={i}
                    condition={condition}
                    packages={packages}
                    onChange={(c) => updateCondition(i, c)}
                    onRemove={() => removeCondition(i)}
                  />
                ))}
              </div>
              <button type="button" onClick={addCondition} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                <Plus className="h-3.5 w-3.5" />
                Add condition
              </button>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium">Manually add users</p>
              <ManualUserPicker userIds={manualUserIds} onChange={setManualUserIds} />
            </div>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
          {droppedConditions > 0 && (
            <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
              {droppedConditions === 1 ? 'One condition has' : `${droppedConditions} conditions have`} no value chosen and
              {droppedConditions === 1 ? ' will be' : ' will be'} discarded on save. Choose a value to keep
              {droppedConditions === 1 ? ' it' : ' them'}.
            </p>
          )}
          {!canSave && name.trim() && (
            <p className="mt-3 text-sm text-muted-foreground">
              Add a condition with a value, or pick users by hand, before saving.
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || !canSave}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
